// IndexedDB persistence layer — survives "clear history" in browsers.
// Exposes the same logical keys as the old localStorage approach.
//
// DB: "midiario_db"  version 1
// Object store: "kv"  keyPath: "k"  — stores { k, v } pairs

const DB_NAME    = "midiario_db";
const DB_VERSION = 1;
const STORE      = "kv";

let _db = null;

function openDB() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "k" });
      }
    };
    req.onsuccess = e => {
      _db = e.target.result;
      // Reset cached connection if browser closes it unexpectedly
      _db.onclose = () => { _db = null; };
      _db.onerror = () => { _db = null; };
      resolve(_db);
    };
    req.onerror = e => reject(e.target.error);
  });
}

export async function dbGet(key, fallback) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx  = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result?.v ?? fallback);
      req.onerror   = () => resolve(fallback);
    });
  } catch {
    // Fallback to localStorage if IndexedDB unavailable (private mode edge cases)
    try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; }
    catch { return fallback; }
  }
}

export async function dbSet(key, value) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx  = db.transaction(STORE, "readwrite");
      const req = tx.objectStore(STORE).put({ k: key, v: value });
      req.onsuccess = () => resolve();
      req.onerror   = e => reject(e.target.error);
    });
  } catch {
    localStorage.setItem(key, JSON.stringify(value));
  }
}

// Migrate existing localStorage data into IndexedDB (runs once on first load)
export async function migrateFromLocalStorage() {
  const KEYS = [
    "reconectados_journal",
    "reconectados_morning",
    "reconectados_moments",
    "reconectados_streak",
    "reconectados_last_entry",
  ];
  const migrated = await dbGet("__migrated__", false);
  if (migrated) return;

  for (const key of KEYS) {
    const raw = localStorage.getItem(key);
    if (raw !== null) {
      try {
        await dbSet(key, JSON.parse(raw));
      } catch {
        await dbSet(key, raw);
      }
    }
  }
  await dbSet("__migrated__", true);
}
